import React, { useState } from "react";
import { Smartphone, CheckCircle, HelpCircle, Search } from "lucide-react";
import AppleLogo from "./assets/apple.png";
import AndroidLogo from "./assets/android.png";
import StepOne from "./assets/one.png";
import StepTwo from "./assets/two.png";
import StepThree from "./assets/three.png";
import { Link } from "react-router-dom";
import PhoneImage from "./assets/phone.png";
import { useTranslation } from "./useTranslation";

const Installation = () => {
  const { t } = useTranslation();
  const [selectedPlatform, setSelectedPlatform] = useState('iphone');
  const [searchTerm, setSearchTerm] = useState('');
  const [installationType, setInstallationType] = useState('qr'); // 'qr' or 'manual'

  const iPhoneDevices = [
    'iPhone XR','iPhone XS','iPhone XS Max','iPhone 11','iPhone 11 Pro','iPhone 11 Pro Max',
    'iPhone SE 2 (2020)','iPhone 12','iPhone 12 Mini','iPhone 12 Pro','iPhone 12 Pro Max',
    'iPhone 13','iPhone 13 Mini','iPhone 13 Pro','iPhone 13 Pro Max','iPhone SE 3 (2022)',
    'iPhone 14','iPhone 14 Plus','iPhone 14 Pro','iPhone 14 Pro Max','iPhone 15','iPhone 15 Plus',
    'iPhone 15 Pro','iPhone 15 Pro Max','iPhone 16','iPhone 16 Plus','iPhone 16 Pro','iPhone 16 Pro Max',
    'iPhone 16e','iPhone 17','iPhone 17 Plus','iPhone 17 Pro','iPhone 167 Pro Max','iPhone 17e'
  ];

  const iPadDevices = [
    'iPad Pro 11″ (model A2068, from 2020)','iPad Pro 12.9″ (model A2069, from 2020)',
    'iPad Air (model A2123, from 2019)','iPad (model A2198, from 2019)',
    'iPad Mini (model A2124, from 2019)','iPad 10th generation (from 2022)'
  ];

  const androidDevices = {
    'Samsung': [
      'Samsung Galaxy S20','Samsung Galaxy S20+','Samsung Galaxy S20+ 5G','Samsung Galaxy S20 Ultra',
      'Samsung Galaxy S20 Ultra 5G','Samsung Galaxy S21','Samsung Galaxy S21+ 5G','Samsung Galaxy S21+ Ultra 5G',
      'Samsung Galaxy S22','Samsung Galaxy S22+','Samsung Galaxy S22 Ultra','Samsung Galaxy S23',
      'Samsung Galaxy S23+','Samsung Galaxy S23 Ultra','Samsung Galaxy S23 FE','Samsung Galaxy S24',
      'Samsung Galaxy S24+','Samsung Galaxy S24 Ultra','Samsung Galaxy S24 FE','Samsung Galaxy S25',
      'Samsung Galaxy S25+','Samsung Galaxy S25 Ultra','Samsung Galaxy S25 Edge','Samsung Galaxy Note 20',
      'Samsung Galaxy Note 20 Ultra 5G','Samsung Galaxy Fold','Samsung Galaxy Z Fold2 5G',
      'Samsung Galaxy Z Fold3 5G','Samsung Galaxy Z Fold4','Samsung Galaxy Z Fold5 5G',
      'Samsung Galaxy Z Fold6 5G','Samsung Galaxy Z Fold7','Samsung Galaxy Z Flip',
      'Samsung Galaxy Z Flip3 5G','Samsung Galaxy Z Flip4','Samsung Galaxy Z Flip5 5G',
      'Samsung Galaxy Z Flip6 5G','Samsung Galaxy Z Flip7','Samsung Galaxy Z Flip7 FE',
      'Samsung Galaxy A54','Samsung Galaxy A55 5G','Samsung Galaxy A35','Samsung Galaxy A56','Samsung Galaxy A36'
    ],
    'Google Pixel': [
      'Google Pixel 2 (Google Fi only)','Google Pixel 2 XL','Google Pixel 3','Google Pixel 3 XL',
      'Google Pixel 3a','Google Pixel 3a XL','Google Pixel 4','Google Pixel 4a','Google Pixel 4 XL',
      'Google Pixel 5','Google Pixel 5a','Google Pixel 6','Google Pixel 6a','Google Pixel 6 Pro',
      'Google Pixel 7a','Google Pixel 7','Google Pixel 7 Pro','Google Pixel 8a','Google Pixel 8',
      'Google Pixel 8 Pro','Google Pixel Fold','Google Pixel 9','Google Pixel 9 Pro','Google Pixel 9 Pro XL'
    ],
    'Huawei': ['Huawei P40','Huawei P40 Pro','Huawei Mate 40 Pro','Huawei Pura 70 Pro'],
    'Oppo': [
      'Oppo Find X3','Oppo Find X3 Pro','Find N2 Flip','Oppo Reno 5A','Oppo Reno 6 Pro 5G','Oppo Reno 9A',
      'Oppo Find X5','Oppo Find X5 Pro','Oppo A55s 5G','Oppo Find N3','Oppo Find N3 Flip','Oppo Find X8',
      'Oppo Find X8 Pro','Oppo Reno14','Oppo Reno14 Pro'
    ],
    'Sony': [
      'Sony Xperia 10 III Lite','Sony Xperia 10 IV','Xperia 10V','Xperia 1 IV','Sony Xperia 5 IV',
      'Sony Xperia 1 V','Sony Xperia Ace III','Sony Xperia 5 V','Sony Xperia 1 VI'
    ],
    'Xiaomi': [
      'Xiaomi 12T Pro','Xiaomi 13','Xiaomi 13 Lite','Xiaomi 13 Pro','Xiaomi 13T','Xiaomi 13T Pro',
      'Xiaomi 14','Xiaomi 14 Pro','Xiaomi 14T','Xiaomi 14T Pro','Xiaomi Redmi Note 13 Pro+',
      'Xiaomi Redmi Note 14 Pro','Xiaomi Redmi Note 14 Pro+','Xiaomi Poco X7','Xiaomi 15','Xiaomi 15 Ultra'
    ],
    'Motorola': [
      'Motorola Razr 2019','Motorola Razr 2022','Motorola Razr 5G','Motorola Razr 40','Motorola Razr 40 Ultra',
      'Motorola Razr+','Motorola Edge 2022','Motorola Edge 2023','Motorola Edge+ (2023)','Motorola Edge 40',
      'Motorola Edge 40 Pro','Motorola Edge 40 Neo','Motorola Edge 50 Pro','Motorola Edge 50 Ultra',
      'Motorola Edge 50 Fusion','Motorola Moto G Power 5G (2024)','Motorola G52J 5G','Motorola G52J 5G Ⅱ',
      'Motorola G53J 5G','Moto G54 5G','Motorola G84','Motorola G34','Motorola Moto G53',
      'Motorola Moto G54','Motorola Razr+ 2024','Motorola Razr 2024','Motorola Moto G Stylus 5G 2024',
      'Motorola Moto G35','Motorola Edge 60','Motorola Edge 60 Pro','Motorola Edge 60 Fusion','Motorola Edge 60 Stylus'
    ],
    'Other Brands': [
      'Sharp AQUOS sense4 lite','Sharp AQUOS Sense6s','AQUOS sense 7','AQUOS sense 7plus','Sharp AQUOS Wish',
      'AQUOS wish 2 SHG08','AQUOS wish3','AQUOS zero 6','Simple Sumaho6','Sharp AQUOS R7','Sharp AQUOS R8',
      'Sharp AQUOS R8 Pro','Sharp Aquos sense8','Rakuten Mini','Rakuten Big-S','Rakuten Big','Rakuten Hand',
      'Rakuten Hand 5G','Honor Magic 4 Pro','Honor Magic 5 Pro','Honor Magic 6 Pro','Honor 90','Honor X8',
      'Honor 200 Pro','Honor Magic V2','Honor Magic V3','Honor 400 Lite','Vivo X80 Pro','Vivo X90 Pro',
      'Vivo X100 Pro','Vivo V29','Vivo V29 Lite','Vivo V29 Lite 5G','Vivo V40','Vivo V40 lite',
      'Vivo V40 SE','Vivo X200','Vivo X200s','Vivo X200 Pro','Vivo X200 FE','Gemini PDA','Fairphone 4',
      'Fairphone 5','DOOGEE V30','OnePlus Open','OnePlus 11','OnePlus 12','OnePlus 13','OnePlus 13R',
      'OnePlus 13T','HAMMER Blade 3','HAMMER Explorer PRO','HAMMER Blade 5G','Nokia XR21','Nokia X30',
      'Nokia G60 5G','myPhone NOW eSIM','OUKITEL WP30 Pro','OUKITEL WP33 Pro','Nuu X5','ZTE Nubia Flip',
      'TLC 50 5G','Asus ROG Phone 9','Asus ROG Phone 9 Pro','Asus Zenfone 12 Ultra','Realme 14 Pro+',
      'Realme GT 7','Nothing Phone 3','Nothing Phone 3a Pro'
    ]
  };

  const getAllAndroidDevices = () => Object.values(androidDevices).flat();

  const filterDevices = (devices, q) =>
    !q ? devices : devices.filter(d => d.toLowerCase().includes(q.toLowerCase()));

  const iPhoneSteps = [
    { step: "01", title: t('installGuide.steps.ios.s1.title'), description: t('installGuide.steps.ios.s1.desc'), detail: t('installGuide.steps.ios.s1.detail') },
    { step: "02", title: t('installGuide.steps.ios.s2.title'), description: t('installGuide.steps.ios.s2.desc'), detail: t('installGuide.steps.ios.s2.detail') },
    { step: "03", title: t('installGuide.steps.ios.s3.title'), description: t('installGuide.steps.ios.s3.desc'), detail: t('installGuide.steps.ios.s3.detail') },
    { step: "04", title: t('installGuide.steps.ios.s4.title'), description: t('installGuide.steps.ios.s4.desc'), detail: t('installGuide.steps.ios.s4.detail') },
    { step: "05", title: t('installGuide.steps.ios.s5.title'), description: t('installGuide.steps.ios.s5.desc'), detail: t('installGuide.steps.ios.s5.detail') },
    { step: "06", title: t('installGuide.steps.ios.s6.title'), description: t('installGuide.steps.ios.s6.desc'), detail: t('installGuide.steps.ios.s6.detail') }
  ];

  const androidSteps = [
    { step: "01", title: t('installGuide.steps.android.s1.title'), description: t('installGuide.steps.android.s1.desc'), detail: t('installGuide.steps.android.s1.detail') },
    { step: "02", title: t('installGuide.steps.android.s2.title'), description: t('installGuide.steps.android.s2.desc'), detail: t('installGuide.steps.android.s2.detail') },
    { step: "03", title: t('installGuide.steps.android.s3.title'), description: t('installGuide.steps.android.s3.desc'), detail: t('installGuide.steps.android.s3.detail') },
    { step: "04", title: t('installGuide.steps.android.s4.title'), description: t('installGuide.steps.android.s4.desc'), detail: t('installGuide.steps.android.s4.detail') },
    { step: "05", title: t('installGuide.steps.android.s5.title'), description: t('installGuide.steps.android.s5.desc'), detail: t('installGuide.steps.android.s5.detail') },
    { step: "06", title: t('installGuide.steps.android.s6.title'), description: t('installGuide.steps.android.s6.desc'), detail: t('installGuide.steps.android.s6.detail') }
  ];

  const currentSteps = selectedPlatform === 'iphone' ? iPhoneSteps : androidSteps;

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-24">
        {/* Header */}
        <header className="text-center max-w-4xl mx-auto mb-24">
          <h1 className="text-4xl font-semibold tracking-tight text-gray-900 mb-6">
            {t('installGuide.header.title')}
          </h1>
          <p className="text-lg leading-relaxed text-gray-600 mb-8">
            {t('installGuide.header.subtitle')} You can check also our
            <Link to="/ios-guide" className="text-blue-500 hover:text-blue-600 ml-1 cursor-pointer">
              {t('installGuide.header.ios')} 
            </Link> and our
            <Link to="/android-guide" className="text-blue-500 hover:text-blue-600 ml-1 cursor-pointer">
              {t('installGuide.header.android')} 
            </Link> 
          </p>
        </header>

        <section className="mb-24">
          <div className="text-center mb-16">
            <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
              {t('installGuide.iphoneHero')}
            </h1>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-3xl p-8 text-center">
              <div className="mb-8 flex justify-center">
                <img src={StepOne} alt={t('installGuide.cards.c1.alt')} className="w-32 h-auto" />
              </div>
              <p className="text-base text-gray-700 leading-relaxed">
                <span className="font-semibold">{t('installGuide.cards.c1.install')}</span>{' '}
                {t('installGuide.cards.c1.textA')}{' '}
                <span className="font-semibold">{t('installGuide.cards.c1.activate')}</span>{' '}
                {t('installGuide.cards.c1.textB')}
              </p>
            </div>

            <div className="bg-gray-50 rounded-3xl p-8 text-center">
              <div className="mb-8 flex justify-center">
                <img src={StepTwo} alt={t('installGuide.cards.c2.alt')} className="w-32 h-auto" />
              </div>
              <p className="text-base text-gray-700 leading-relaxed">
                {t('installGuide.cards.c2.line1')}<br />
                {t('installGuide.cards.c2.line2')}<br />
                {t('installGuide.cards.c2.line3')}
              </p>
            </div>

            <div className="bg-gray-50 rounded-3xl p-8 text-center">
              <div className="mb-8 flex justify-center">
                <img src={StepThree} alt={t('installGuide.cards.c3.alt')} className="w-32 h-auto" />
              </div>
              <p className="text-base text-gray-700 leading-relaxed">
                {t('installGuide.cards.c3.line1')}<br />
                {t('installGuide.cards.c3.line2')}
              </p>
            </div>
          </div>
        </section>

        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
              {t('installGuide.learn.title')}
            </h2>
            <div className="flex items-center justify-center gap-2 mb-8">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
            </div> 
            <p className="text-lg text-gray-700 mb-8">
              {t('installGuide.learn.subtitle')}
            </p>

            <div className="flex justify-center gap-4 mb-12">
              <button
                onClick={() => setInstallationType('qr')}
                className={`flex items-center gap-3 px-8 py-4 rounded-2xl font-semibold transition-all ${
                  installationType === 'qr'
                    ? 'bg-gray-100 text-gray-900 border-2 border-gray-300'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              > 
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                {t('installGuide.learn.btnQr')}
              </button>
              <button
                onClick={() => setInstallationType('manual')}
                className={`flex items-center gap-3 px-8 py-4 rounded-2xl font-semibold transition-all ${
                  installationType === 'manual'
                    ? 'bg-gray-100 text-gray-900 border-2 border-gray-300'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('installGuide.learn.btnManual')}
              </button>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200">
              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <img src={PhoneImage} alt={t('installGuide.prep.alt')} className="w-24 h-auto" />
                </div>
                <div className="flex-1">
                  {installationType === 'qr' ? (
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">
                        {t('installGuide.prep.qr.title')}
                      </h3>
                      <ul className="space-y-3 text-base text-gray-700">
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-3 flex-shrink-0"></div>
                          <span>{t('installGuide.prep.qr.i1')}</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-3 flex-shrink-0"></div>
                          <span>{t('installGuide.prep.qr.i2')}</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-3 flex-shrink-0"></div>
                          <span>{t('installGuide.prep.qr.i3')}</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-3 flex-shrink-0"></div>
                          <span>{t('installGuide.prep.qr.i4')}</span>
                        </li>
                      </ul>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">
                        {t('installGuide.prep.manual.title')}
                      </h3>
                      <ul className="space-y-3 text-base text-gray-700">
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{t('installGuide.prep.manual.i1')}</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{t('installGuide.prep.shared.i2')}</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{t('installGuide.prep.shared.i3')}</span>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
              {t('installGuide.start.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <div className="mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <span className="font-semibold text-black">01.</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {t('installGuide.start.c1.title')}
                </h3>
              </div>
              {installationType === 'qr' ? (
                <div className="text-gray-700 space-y-3">
                  <p>{t('installGuide.start.c1.qr.p1')}</p>
                  <p className="text-sm">
                    {t('installGuide.start.c1.shared.missing')}{' '}
                    <a href="#" className="text-blue-600 underline">{t('installGuide.start.c1.shared.contact')}</a>.
                  </p>
                </div>
              ) : (
                <div className="text-gray-700 space-y-3">
                  <p>{t('installGuide.start.c1.manual.p1')}</p>
                  <p className="text-sm">
                    {t('installGuide.start.c1.shared.missing')}{' '}
                    <a href="#" className="text-blue-600 underline">{t('installGuide.start.c1.shared.contact')}</a>.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <div className="mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <span className="font-semibold text-black">02.</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {installationType === 'qr' ? t('installGuide.start.c2.qr.title') : t('installGuide.start.c2.manual.title')}
                </h3>
              </div>
              {installationType === 'qr' ? (
                <div className="text-gray-700">
                  <p>{t('installGuide.start.c2.qr.p1')}</p>
                </div>
              ) : (
                <div className="text-gray-700">
                  <p>{t('installGuide.start.c2.manual.p1')}</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <div className="mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <span className="font-semibold text-black">03.</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {t('installGuide.start.c3.title')}
                </h3>
              </div>
              {installationType === 'qr' ? (
                <div className="text-gray-700">
                  <p>{t('installGuide.start.c3.qr.p1')}</p>
                </div>
              ) : (
                <div className="text-gray-700">
                  <p>{t('installGuide.start.c3.manual.p1')}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Device Compatibility Section */}
        <section className="mb-24">
          <div className="bg-gray-50 rounded-3xl shadow-sm p-8 mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Smartphone className="text-gray-600" size={28} />
              <h2 className="text-2xl font-semibold text-gray-900">
                {t('installGuide.compat.title')}
              </h2>
            </div>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              {t('installGuide.compat.subtitle')}
            </p>

            {/* Platform Selection */}
            <div className="flex justify-center gap-4 mb-8">
              <button
                onClick={() => { setSelectedPlatform('iphone'); setSearchTerm(''); }}
                className={`flex items-center gap-3 px-8 py-4 rounded-2xl font-semibold transition-all ${
                  selectedPlatform === 'iphone'
                    ? 'bg-white text-gray-900 border-2 border-gray-900'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }`}
              >
                <img src={AppleLogo} className="w-12 h-auto" />
                {t('installGuide.compat.iosTab')}
              </button>
              <button
                onClick={() => { setSelectedPlatform('android'); setSearchTerm(''); }}
                className={`flex items-center gap-3 px-8 py-4 rounded-2xl font-semibold transition-all ${
                  selectedPlatform === 'android'
                    ? 'bg-white text-gray-900 border-2 border-gray-900'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }`}
              >
                <img src={AndroidLogo} className="w-12 h-auto" />
                {t('installGuide.compat.androidTab')}
              </button>
            </div>

            {/* Search Bar */}
            <div className="max-w-md mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder={
                    selectedPlatform === 'iphone'
                      ? t('installGuide.compat.searchIos')
                      : t('installGuide.compat.searchAndroid')
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Device Lists - Only show when user starts typing */}
            {searchTerm.trim() && (
              <>
                {selectedPlatform === 'iphone' ? (
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        {t('installGuide.compat.iphones')}
                      </h3>
                      <div className="bg-white rounded-2xl p-4 max-h-80 overflow-y-auto border border-gray-200">
                        {filterDevices(iPhoneDevices, searchTerm).map((device, index) => (
                          <div key={index} className="py-2 px-3 border-b border-gray-100 last:border-b-0">
                            <span className="text-gray-600 mr-2">✓</span>
                            <span className="text-gray-700">{device}</span>
                          </div>
                        ))}
                        {filterDevices(iPhoneDevices, searchTerm).length === 0 && (
                          <p className="text-gray-500 text-center py-4">
                            {t('installGuide.compat.noMatch')}
                          </p>
                        )}
                      </div>

                      {filterDevices(iPhoneDevices, searchTerm).length > 0 && (
                        <div className="mt-6 p-4 bg-gray-100 rounded-xl border border-gray-300">
                          <h4 className="font-semibold text-gray-900 mb-2">
                            {t('installGuide.compat.iosNotesTitle')}
                          </h4>
                          <ul className="text-sm text-gray-700 space-y-1">
                            <li>• {t('installGuide.compat.iosNotes.i1')}</li>
                            <li>• {t('installGuide.compat.iosNotes.i2')}</li>
                            <li>• {t('installGuide.compat.iosNotes.i3')}</li>
                          </ul>
                        </div>
                      )}
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        {t('installGuide.compat.ipads')}
                      </h3>
                      <div className="bg-white rounded-2xl p-4 border border-gray-200">
                        {filterDevices(iPadDevices, searchTerm).map((device, index) => (
                          <div key={index} className="py-2 px-3 border-b border-gray-100 last:border-b-0">
                            <span className="text-gray-600 mr-2">✓</span>
                            <span className="text-gray-700">{device}</span>
                          </div>
                        ))}
                        {filterDevices(iPadDevices, searchTerm).length === 0 && (
                          <p className="text-gray-500 text-center py-4">
                            {t('installGuide.compat.noMatch')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {t('installGuide.compat.androids')}
                    </h3>
                    <div className="bg-white rounded-2xl p-4 max-h-96 overflow-y-auto border border-gray-200">
                      {filterDevices(getAllAndroidDevices(), searchTerm).map((device, index) => (
                        <div key={index} className="py-2 px-3 border-b border-gray-100 last:border-b-0">
                          <span className="text-gray-600 mr-2">✓</span>
                          <span className="text-gray-700">{device}</span>
                        </div>
                      ))}
                      {filterDevices(getAllAndroidDevices(), searchTerm).length === 0 && (
                        <p className="text-gray-500 text-center py-4">
                          {t('installGuide.compat.noMatch')}
                        </p>
                      )}
                    </div>

                    {filterDevices(getAllAndroidDevices(), searchTerm).length > 0 && (
                      <div className="mt-6 p-4 bg-gray-100 rounded-xl border border-gray-300">
                        <h4 className="font-semibold text-gray-900 mb-2">
                          {t('installGuide.compat.androidNotesTitle')}
                        </h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                          <li>• {t('installGuide.compat.androidNotes.i1')}</li>
                          <li>• {t('installGuide.compat.androidNotes.i2')}</li>
                          <li>• {t('installGuide.compat.androidNotes.i3')}</li>
                          <li>• {t('installGuide.compat.androidNotes.i4')}</li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Empty state when no search term */}
            {!searchTerm.trim() && (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <Search className="text-gray-400" size={24} />
                </div>
                <p className="text-gray-500 text-base">
                  {t('installGuide.compat.empty')}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Pro Tips */}
        <section className="mb-24">
          <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle className="text-gray-600" size={28} />
              <h2 className="text-2xl font-semibold text-gray-900">
                {t('installGuide.tips.title')}
              </h2>
            </div>
            <p className="text-base text-gray-700 leading-relaxed mb-8">
              {t('installGuide.tips.body')}
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-2xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4">
                  {t('installGuide.tips.before.title')}
                </h3>
                <ul className="text-gray-700 space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{t('installGuide.tips.before.i1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{t('installGuide.tips.before.i2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{t('installGuide.tips.before.i3')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{t('installGuide.tips.before.i4')}</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4">
                  {t('installGuide.tips.after.title')}
                </h3>
                <ul className="text-gray-700 space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{t('installGuide.tips.after.i1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{t('installGuide.tips.after.i2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{t('installGuide.tips.after.i3')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{t('installGuide.tips.after.i4')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Support Section */}
        <section className="text-center">
          <div className="bg-gray-50 rounded-3xl p-12 border border-gray-200">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                <HelpCircle className="text-gray-600" size={32} />
              </div>
            </div>
            <h2 className="text-2xl font-semibold mb-6 text-gray-900">
              {t('installGuide.support.title')}
            </h2>
            <p className="text-lg leading-relaxed mb-8 max-w-2xl mx-auto text-gray-700">
              {t('installGuide.support.body')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:support@routedlabs.com"
                className="px-8 py-4 bg-white text-gray-900 border border-gray-900 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                {t('installGuide.support.btnEmail')}
              </a>
              <a
                href="/support"
                className="px-8 py-4 bg-gray-100 text-gray-900 rounded-xl font-semibold hover:bg-gray-200 transition-colors border border-gray-300"
              >
                {t('installGuide.support.btnCenter')}
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Installation;
